import { EventEmitter } from "events";
import {
  DesktopAgentMessage,
  DesktopAgentResponse,
  DesktopAgentConnectionState,
} from "./DesktopAgentTypes";

/**
 * DesktopAgentClient - Layer 1: Transport Layer
 *
 * Handles WebSocket communication with the local Desktop Agent.
 * This class is responsible ONLY for transport - no business logic.
 *
 * Events emitted:
 * - 'connected': When connection is established
 * - 'disconnected': When connection is lost
 * - 'reconnecting': When attempting to reconnect (payload: { attempt: number })
 * - 'error': When an error occurs (payload: Error)
 * - 'message': When a message is received (payload: DesktopAgentResponse)
 */
export class DesktopAgentClient extends EventEmitter {
  private ws: WebSocket | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 2000; // Start with 2 seconds
  private maxReconnectDelay = 30000; // Max 30 seconds
  private connectionState: DesktopAgentConnectionState =
    DesktopAgentConnectionState.DISCONNECTED;
  private pendingRequests: Map<
    string,
    {
      resolve: (data: unknown) => void;
      reject: (error: Error) => void;
      timeout: NodeJS.Timeout;
    }
  > = new Map();
  private readonly defaultTimeout = 30000; // 30 seconds
  private connectionPromise: Promise<void> | null = null;
  private connectionResolve: (() => void) | null = null;
  private connectionReject: ((error: Error) => void) | null = null;

  constructor(
    private url: string = "ws://localhost:8765",
    private autoConnect: boolean = false
  ) {
    super();
    // Only auto-connect if explicitly enabled AND in browser
    if (this.autoConnect && typeof window !== "undefined") {
      this.connect();
    }
  }

  /**
   * Connect to the Desktop Agent
   * Returns a promise that resolves when connected or rejects on error
   * @param force - If true, forces a new connection even if state says connected
   */
  public connect(force = false): Promise<void> {
    // SSR guard - cannot connect on server
    if (typeof window === "undefined") {
      return Promise.resolve();
    }

    // Use isConnected() which checks actual WebSocket state, not just the state variable
    // This fixes the bug where state says CONNECTED but WebSocket is actually dead
    if (!force && this.isConnected()) {
      // eslint-disable-next-line no-console
      console.log(
        "[DesktopAgentClient] Already connected (verified via WebSocket state)"
      );
      return Promise.resolve();
    }

    // If state says CONNECTED but isConnected() returned false, we have a stale state
    // Log this for debugging and proceed with reconnection
    if (
      this.connectionState === DesktopAgentConnectionState.CONNECTED &&
      !this.isConnected()
    ) {
      // eslint-disable-next-line no-console
      console.log(
        "[DesktopAgentClient] ⚠️ State mismatch detected: state=CONNECTED but WebSocket is not open. Forcing reconnection."
      );
      // Clean up the stale WebSocket reference
      if (this.ws) {
        try {
          this.ws.close();
        } catch {
          // Ignore close errors on stale socket
        }
        this.ws = null;
      }
      this.connectionState = DesktopAgentConnectionState.DISCONNECTED;
    }

    // If already connecting and not forcing, return existing promise
    if (
      !force &&
      this.connectionState === DesktopAgentConnectionState.CONNECTING &&
      this.connectionPromise
    ) {
      return this.connectionPromise;
    }

    // If forcing while connecting, clean up and start fresh
    if (force && this.connectionPromise) {
      // eslint-disable-next-line no-console
      console.log(
        "[DesktopAgentClient] Force reconnect requested, cleaning up..."
      );
      if (this.ws) {
        try {
          this.ws.close();
        } catch {
          // Ignore close errors
        }
        this.ws = null;
      }
      this.connectionPromise = null;
      this.connectionResolve = null;
      this.connectionReject = null;
    }

    this.connectionState = DesktopAgentConnectionState.CONNECTING;

    // Create a promise that will resolve when connected
    this.connectionPromise = new Promise((resolve, reject) => {
      this.connectionResolve = resolve;
      this.connectionReject = reject;

      try {
        // eslint-disable-next-line no-console
        console.log(
          `[DesktopAgentClient] Attempting to connect to ${this.url}...`
        );
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          this.handleOpen();
        };

        this.ws.onmessage = (event: MessageEvent) => {
          this.handleMessage(event);
        };

        this.ws.onclose = () => {
          this.handleClose();
        };

        this.ws.onerror = (event: Event) => {
          // Suppress the error - handleError will emit it as an event
          // This prevents uncaught errors from crashing the app
          this.handleError(event);
        };
      } catch (error) {
        // Don't throw - emit error event and attempt reconnection
        this.handleError(error as Error);
        this.scheduleReconnect();
      }
    });

    return this.connectionPromise;
  }

  /**
   * Disconnect from the Desktop Agent
   */
  public disconnect(): void {
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Reject all pending requests
    this.pendingRequests.forEach((request) => {
      clearTimeout(request.timeout);
      request.reject(new Error("Disconnected"));
    });
    this.pendingRequests.clear();

    this.connectionState = DesktopAgentConnectionState.DISCONNECTED;
  }

  /**
   * Send a message to the Desktop Agent and wait for response
   * @param action - The action to perform
   * @param payload - Optional payload data
   * @returns Promise that resolves with the response data
   */
  public async send<T = unknown>(
    action: string,
    payload?: unknown
  ): Promise<T> {
    if (!this.isConnected()) {
      throw new Error("Not connected to Desktop Agent");
    }

    const requestId = this.generateRequestId();
    const message: DesktopAgentMessage = {
      action,
      requestId,
      payload,
    };

    return new Promise<T>((resolve, reject) => {
      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Request timeout: ${action}`));
      }, this.defaultTimeout);

      // Store pending request
      this.pendingRequests.set(requestId, { resolve, reject, timeout });

      // Send message
      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.ws!.send(JSON.stringify(message));
      } catch (error) {
        this.pendingRequests.delete(requestId);
        clearTimeout(timeout);
        reject(error);
      }
    });
  }

  /**
   * Check if connected to Desktop Agent
   */
  public isConnected(): boolean {
    return (
      this.ws !== null &&
      this.ws.readyState === WebSocket.OPEN &&
      this.connectionState === DesktopAgentConnectionState.CONNECTED
    );
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): DesktopAgentConnectionState {
    return this.connectionState;
  }

  // ===== PRIVATE METHODS =====

  private handleOpen(): void {
    // eslint-disable-next-line no-console
    console.log("[DesktopAgentClient] Connection established");
    this.connectionState = DesktopAgentConnectionState.CONNECTED;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 2000; // Reset delay

    // Resolve the connection promise if pending
    if (this.connectionResolve) {
      this.connectionResolve();
      this.connectionResolve = null;
      this.connectionReject = null;
    }

    this.emit("connected");
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const response: DesktopAgentResponse = JSON.parse(event.data);

      // Emit generic message event
      this.emit("message", response);

      // Match response to pending request
      const pending = this.pendingRequests.get(response.requestId);
      if (pending) {
        clearTimeout(pending.timeout);
        this.pendingRequests.delete(response.requestId);

        if (response.status === "success") {
          pending.resolve(response.data);
        } else {
          // Include details in error message for better debugging
          const errorMessage = response.details
            ? `${response.error}: ${response.details}`
            : response.error || "Unknown error";
          pending.reject(new Error(errorMessage));
        }
      }
    } catch (error) {
      this.emit("error", new Error(`Failed to parse message: ${error}`));
    }
  }

  private handleClose(): void {
    const wasConnected =
      this.connectionState === DesktopAgentConnectionState.CONNECTED;
    const wasConnecting =
      this.connectionState === DesktopAgentConnectionState.CONNECTING;

    this.connectionState = DesktopAgentConnectionState.DISCONNECTED;
    this.ws = null;

    // Reject pending connection promise if we were connecting
    if (wasConnecting && this.connectionReject) {
      this.connectionReject(
        new Error("WebSocket connection closed before established")
      );
      this.connectionResolve = null;
      this.connectionReject = null;
    }

    // Clear the connection promise
    this.connectionPromise = null;

    if (wasConnected) {
      // eslint-disable-next-line no-console
      console.log("[DesktopAgentClient] Disconnected from Desktop Agent");
      this.emit("disconnected");
    }

    // Attempt to reconnect
    this.scheduleReconnect();
  }

  private handleError(error: Event | Error): void {
    // Create a detailed error message
    let errorMessage = "WebSocket connection failed";

    if (error instanceof Error) {
      errorMessage = `${errorMessage}: ${error.message}`;
    } else if (error instanceof Event) {
      // WebSocket error event doesn't have much detail, but we can provide context
      errorMessage = `${errorMessage} to ${this.url}`;

      if (this.ws) {
        switch (this.ws.readyState) {
          case WebSocket.CONNECTING:
            errorMessage +=
              " (connection attempt failed - is Desktop Agent running?)";
            break;
          case WebSocket.CLOSED:
            errorMessage += " (connection closed)";
            break;
          case WebSocket.CLOSING:
            errorMessage += " (connection closing)";
            break;
          default:
            break;
        }
      }
    }

    const err = new Error(errorMessage);
    // eslint-disable-next-line no-console
    console.warn("[DesktopAgentClient] Error:", errorMessage);

    // Only update state if not already reconnecting
    if (this.connectionState !== DesktopAgentConnectionState.RECONNECTING) {
      this.connectionState = DesktopAgentConnectionState.ERROR;
    }

    // Reject the connection promise if pending
    if (this.connectionReject) {
      this.connectionReject(err);
      this.connectionResolve = null;
      this.connectionReject = null;
    }

    // Emit error event but don't throw - let listeners handle it
    this.emit("error", err);

    // WebSocket errors usually mean connection failed - trigger reconnection
    if (this.ws && this.ws.readyState === WebSocket.CLOSED) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit(
        "error",
        new Error("Max reconnection attempts reached. Giving up.")
      );
      return;
    }

    this.clearReconnectTimer();

    this.reconnectAttempts += 1;
    this.connectionState = DesktopAgentConnectionState.RECONNECTING;
    this.emit("reconnecting", { attempt: this.reconnectAttempts });

    // Exponential backoff
    const delay = Math.min(
      this.reconnectDelay * 1.5 ** (this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect().catch(() => {
        // Connection failed, will retry again via handleClose
      });
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Note: No singleton export - use DesktopAgentManager to create and manage instances
